import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { color } from 'styled-system';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import { DateTime, Interval } from 'luxon';

import { hexToRgb } from '../Utils/utils';
import { Text } from '../SubComponents/Text';
import { colors } from '../Style/Theme';

const getPathColor = (percentage: number) => {
  if (percentage < 25) return colors.red;
  if (percentage < 75) return colors.yellow;

  return colors.green;
};

const getTimeRemaining = (now: DateTime | null, expires: DateTime | null) => {
  if (!now || !expires || expires <= now) return '00:00';

  const timeRemaining = Interval.fromDateTimes(now, expires)
    .toDuration()
    .toFormat('mm:ss');

  return timeRemaining;
};

const getPercentage = (
  modified: DateTime | null,
  now: DateTime | null,
  expires: DateTime | null
) => {
  if (!modified || !now || !expires || expires <= now) return 0;

  const baseDifference = (expires.toMillis() - modified.toMillis()) / 1000;
  const currentDifference = (expires.toMillis() - now.toMillis()) / 1000;

  const percentage = Number(((100 / baseDifference) * currentDifference).toFixed(0));

  return percentage;
};

const useBookingTimer = (modifiedTime: string, expiresTime: string) => {
  const modified = modifiedTime ? DateTime.fromISO(modifiedTime) : null;
  const expires = expiresTime ? DateTime.fromISO(expiresTime) : null;
  const defaultNow = modifiedTime || expiresTime ? DateTime.now() : null;

  const [state, setState] = useState({
    timeRemaining: getTimeRemaining(defaultNow, expires),
    percentage: getPercentage(modified, defaultNow, expires),
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (modified && expires) {
        const now = DateTime.now();

        setState({
          timeRemaining: getTimeRemaining(now, expires),
          percentage: getPercentage(modified, now, expires),
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [modified, expires]);

  return state;
};

export interface BookingTimerClockProps {
  className?: string;
  modifiedDate: string;
  expiresDate: string;
}

const BookingTimerClock = (props: BookingTimerClockProps) => {
  const { className, modifiedDate, expiresDate } = props;
  const { timeRemaining, percentage } = useBookingTimer(modifiedDate, expiresDate);

  return (
    <div className={className}>
      <CircularProgressbarWithChildren
        value={percentage}
        strokeWidth={7}
        styles={buildStyles({
          pathColor: getPathColor(percentage),
          trailColor: `${hexToRgb(getPathColor(percentage), { opacity: 20 })}`,
          strokeLinecap: 'round',
          pathTransitionDuration: 0.6,
        })}
      >
        <Text m="0">{timeRemaining}</Text>
      </CircularProgressbarWithChildren>
    </div>
  );
};

const StyledBookingTimerClock = styled(BookingTimerClock)`
  display: flex;
  width: 55px;
  height: 55px;
  margin: 0 auto;
  align-self: center;

  ${color}

  > div {
    width: 100%;
    height: 100%;
  }

  .CircularProgressbar + div {
    margin-top: 0 !important;
    top: 0;
  }
`;

export default StyledBookingTimerClock;
